import { useState, useEffect } from "react";
import API from "../../services/api";
import "./Sections.css";

export default function SectionList() {
  const school = JSON.parse(localStorage.getItem("schoolAdmin"));

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedClassName, setSelectedClassName] = useState("");
  const [sectionName, setSectionName] = useState("");
  const [message, setMessage] = useState({ text: "", type: "" });
  const [adding, setAdding] = useState(false);

  const loadClasses = async () => {
    try {
      const res = await API.get("/school-admin/classes/", {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setClasses(res.data.classes || []);
    } catch (err) {
      console.error("Error loading classes", err);
    }
  };

  const loadSections = async (classId) => {
    if (!classId) { setSections([]); return; }
    try {
      const res = await API.get(`/school-admin/sections/${classId}/`, {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setSections(res.data.sections || []);
    } catch (err) {
      console.error("Error loading sections", err);
    }
  };

  useEffect(() => { loadClasses(); }, []);

  const selectClass = (id, name) => {
    setSelectedClass(id);
    setSelectedClassName(name);
    setSectionName("");
    setMessage({ text: "", type: "" });
    loadSections(id);
  };

  const addSection = async () => {
    if (!selectedClass) return setMessage({ text: "Select a class first", type: "error" });
    if (!sectionName.trim()) return setMessage({ text: "Enter section name", type: "error" });
    setAdding(true);
    try {
      await API.post("/school-admin/sections/add/",
        { class_id: selectedClass, name: sectionName },
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );
      setMessage({ text: `Section "${sectionName}" added!`, type: "success" });
      setSectionName("");
      loadSections(selectedClass);
    } catch {
      setMessage({ text: "Error adding section", type: "error" });
    } finally {
      setAdding(false);
    }
  };

  const deleteSection = async (id, name) => {
    if (!window.confirm(`Delete section "${name}"?`)) return;
    try {
      await API.delete(`/school-admin/sections/delete/${id}/`, {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      loadSections(selectedClass);
    } catch {
      setMessage({ text: "Error deleting section", type: "error" });
    }
  };

  const PILL_COLORS = [
    { bg: "#ede9fe", color: "#5b21b6" },
    { bg: "#fce7f3", color: "#9d174d" },
    { bg: "#dcfce7", color: "#166534" },
    { bg: "#dbeafe", color: "#1e40af" },
    { bg: "#fef3c7", color: "#92400e" },
    { bg: "#fee2e2", color: "#991b1b" },
  ];

  return (
    <div className="sc-page">

        {/* HERO */}
        <div className="sc-hero">
          <div className="sc-hero-left">
            <span className="sc-hero-icon">📋</span>
            <div>
              <h1 className="sc-hero-title">Sections</h1>
              <p className="sc-hero-sub">Assign sections to classes and organise students</p>
            </div>
          </div>
        </div>

        <div className="sc-layout">

          {/* LEFT — CLASS PICKER */}
          <div className="sc-classes-panel">
            <p className="sc-panel-label">Select Class</p>
            {classes.length === 0 ? (
              <p className="sc-no-classes">No classes found</p>
            ) : (
              classes.map((c) => (
                <button
                  key={c.id}
                  className={`sc-class-pill${selectedClass === String(c.id) ? " active" : ""}`}
                  onClick={() => selectClass(String(c.id), c.name)}
                >
                  🏛️ {c.name}
                </button>
              ))
            )}
          </div>

          {/* RIGHT — SECTIONS */}
          <div className="sc-sections-panel">
            {!selectedClass ? (
              <div className="sc-pick-prompt">
                <span className="sc-pick-icon">👈</span>
                <p>Select a class to view and manage its sections</p>
              </div>
            ) : (
              <>
                <div className="sc-sections-header">
                  <div>
                    <h3 className="sc-class-name">{selectedClassName}</h3>
                    <p className="sc-count">{sections.length} section{sections.length !== 1 ? "s" : ""}</p>
                  </div>
                </div>

                {message.text && (
                  <div className={`sc-alert sc-alert-${message.type}`}>{message.text}</div>
                )}

                {/* ADD SECTION ROW */}
                <div className="sc-add-row">
                  <input
                    type="text"
                    className="sc-add-input"
                    placeholder="Section name (e.g. A, B, Rose)"
                    value={sectionName}
                    onChange={(e) => setSectionName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addSection()}
                  />
                  <button className="sc-add-btn" onClick={addSection} disabled={adding}>
                    {adding ? "Adding…" : "+ Add"}
                  </button>
                </div>

                {/* SECTIONS PILLS */}
                {sections.length === 0 ? (
                  <div className="sc-empty">
                    <p>📭 No sections yet — add one above</p>
                  </div>
                ) : (
                  <div className="sc-pills-grid">
                    {sections.map((s, i) => {
                      const color = PILL_COLORS[i % PILL_COLORS.length];
                      return (
                        <div key={s.id} className="sc-section-card" style={{ background: color.bg, borderColor: color.color + "44" }}>
                          <span className="sc-section-letter" style={{ color: color.color }}>
                            {s.name}
                          </span>
                          <button
                            className="sc-del-btn"
                            style={{ color: color.color }}
                            onClick={() => deleteSection(s.id, s.name)}
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      </div>
  );
}
